import Languages from '../constants/Languages';
import schema from '../assets/schemas/additionalInfoQuestions.json';
import { formatPhoneNumber } from '../utils/formatPhone';
import { Platform } from 'react-native';
import timeUtils from './timeUtils';
let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

const specialTypeMap = {
  address: ['address_street_1', 'city', 'state'],
  benefits_programs: ['snap_benefits', 'wic_benefits', 'tanf_benefits', 'medicaid_benefits', 'ssi_benefits'],
  tefap_income_eligibility: ['below_min_household_income'],
};
const ignoreFields = ['contact_info', 'household_size', 'benefits_programs', 'tefap_income_eligibility'];
const specialFieldChildFieldMap = {
  address_street_1: {
    question_uuid: 'address_street_1',
    value: (value) => value,
    label: 'address_street_1',
    type: 'string',
  },
  address_street_2: {
    question_uuid: 'address_street_2',
    value: (value) => value,
    label: 'address_street_2',
    type: 'string',
  },
  city: {
    question_uuid: 'city',
    value: (value) => value,
    label: 'city',
    type: 'string',
  },
  state: {
    question_uuid: 'state',
    value: (value) => statesByCode[value],
    label: 'state',
    type: 'select_one',
  },
};

// format based on special UUIDs
const formatters = {
  dietary_preferences: ({ question, value, user }) => formatMultiSelect(question, value, user),
  contact_info: ({ value }) => formatPhoneNumber(value),
  country_of_birth: ({ value }) => timeUtils.formatBirthdate(value),
  zip_code: ({ value, user }) => formatZipCode(value, user),
};

// format based on types
const typeFormatters = {
  telephone_us: ({ value }) => formatPhoneNumber(value),
};

export {
  getAdditionalRequestedFields,
  formatMultiSelect,
  specialTypeMap,
  getSpecialTypeParentField,
  getUserUpdateOptionsAsString,
  formatters,
  typeFormatters,
  expandFields,
  formatZipCode,
  statesByCode,
  getFieldValidationErrors,
  convertToString,
  getQuestionByUUID,
  getLabelAndValueForSpecialField,
  formatField,
  getCheckedList,
  isBlankField,
};

/**
 * Searches through a predefined map of special types to find the parent field (key) for a given child field.
 * The function iterates over the `specialTypeMap` object, checking each array of child fields to find a match for the provided child field name.
 * If a match is found, the corresponding key (representing the parent field) is returned.
 *
 * @param {string} childField - The name of the child field for which to find the corresponding parent field.
 * @returns {string|null} The parent field (key) corresponding to the provided child field, or null if no parent field is found.
 *
 * @example
 * // Returns 'address'
 * getSpecialTypeParentField('address_street_1');
 *
 * @example
 * // Returns null if the child field does not exist in the map
 * getSpecialTypeParentField('unknown_field');
 */
const getSpecialTypeParentField = (childField) => {
  // Iterate over the specialTypeMap to find the parent field
  for (const [key, value] of Object.entries(specialTypeMap)) {
    if (value.includes(childField)) {
      return key; // Return the key (parent field) if the child field is found
    }
  }

  return null; // Return null if the child field is not found in any array
};

/**
 * Formats a given field value using a specific formatter based on the field identifier and updates the user object if necessary.
 * The function selects the appropriate formatter from the `formatters` object using the field identifier. Each formatter handles
 * the value differently based on the field's data type and purpose, such as formatting phone numbers, dietary preferences,
 * birthdates, and zip codes.
 *
 * @param {string} field - The identifier for the field to be formatted. It is used to select the correct formatter
 *                         from the `formatters` object. Expected values include 'dietary_preferences', 'contact_info',
 *                         'country_of_birth', and 'zip_code'.
 * @param {string|number|Object} value - The value to be formatted. The actual type and content of this parameter depend
 *                                       on the specific formatter being used.
 * @param {Object} user - The user object that may be updated with the formatted value for certain fields, like 'zip_code'.
 * @returns {string|undefined} The formatted value for the field. The return type and format depend on the specific formatter
 *                             used. In cases where the formatter directly updates the user object (e.g., 'zip_code'),
 *                             the function may return `undefined`.
 *
 * @example
 * // Assuming formatMultiSelect, formatPhoneNumber, and timeUtils.formatBirthdate are defined elsewhere
 * const user = { dietary_preferences: '', contact_info: '', country_of_birth: '', zip_code: '' };
 * formatField('contact_info', '1234567890', user); // user.contact_info might be updated to a formatted phone number
 * formatField('zip_code', '123456', user); // user.zip_code would be updated to the formatted zip code
 *
 * @see formatMultiSelect, formatPhoneNumber, timeUtils.formatBirthdate, formatZipCode for formatter function details.
 */
const formatField = (question, value, user) => {
  if (typeFormatters[question.type]) {
    return typeFormatters[question.type]({ question, value, user });
  }

  // Assuming `formatters` is an object where keys are field names and values are formatting functions
  return formatters[question.question_uuid]({ question, value, user });
};

/**
 * Formats a given zip code to ensure its maximum length is 5 characters and updates the user object with the formatted zip code.
 * If the input zip code is longer than 5 characters, it truncates the zip code to its first 5 characters.
 * If the zip code is 5 or fewer characters, it updates the user object with the zip code unchanged.
 *
 * @param {string|number} zip - The zip code to be formatted. Can be a string or a number.
 * @param {Object} user - The user object that will be mutated by setting its `zip_code` property to the formatted zip code.
 * @returns {string} The formatted zip code with a maximum length of 5 characters.
 *
 * @example
 * // Assuming user = { name: "Jane Doe", zip_code: "" }
 * // After calling formatZipCode('123456789', user), user.zip_code will be '12345'
 * formatZipCode('123456789', user);
 *
 * @example
 * // Assuming user = { name: "John Doe", zip_code: "" }
 * // After calling formatZipCode('123', user), user.zip_code will be '123'
 * formatZipCode('123', user);
 */
const formatZipCode = (zip, user) => {
  if (!zip) return '';
  const formattedZip = zip.toString().substring(0, 5);
  user.zip_code = formattedZip;
  return formattedZip;
};

/**
 * Updates the checked list based on user input for a specific field. This function first expands the field
 * to potentially multiple fields (in case of compound fields) and then updates the checked list by either
 * removing fields with empty inputs or adding fields with valid inputs.
 *
 * @param {Array<string>} checkedList - The current list of fields that have been checked.
 * @param {Object} user - An object representing the user's inputs, where keys are field names and values are
 *                        the user's input for those fields.
 * @param {string} field - The field being updated. This field will be expanded into multiple fields if necessary
 *                         by the `expandFields` function, and the checked list will be updated accordingly.
 *
 * @returns {Array<string>} The updated checked list, with fields added or removed based on the user's input.
 *
 * @example
 * const checkedList = ['name', 'email'];
 * const user = { name: 'Alice', email: '', phone: '1234567890' };
 * const updatedList = getCheckedList(checkedList, user, 'phone');
 * // Assuming 'phone' is not a compound or special field, the updatedList would be ['name', 'phone']
 */
const getCheckedList = (checkedList, user, field) => {
  const updatedFields = expandFields([field]);
  let newCheckList = [...checkedList];

  updatedFields.forEach((expandedField) => {
    if (isBlankField(user[expandedField])) {
      newCheckList = newCheckList.filter((target) => target !== expandedField);
    } else {
      newCheckList = [...new Set([...newCheckList, expandedField])];
    }
  });

  return newCheckList;
};

/**
 * Checks if a given field value is blank or empty based on predefined criteria.
 *
 * An input is considered blank/empty if it is an empty string, undefined, null, or an empty array.
 *
 * @param userFieldValue current user-set value of an input field.
 * @returns {boolean} true if the field value is one of the values considered blank, false otherwise.
 */
const isBlankField = (userFieldValue) => {
  return (
    userFieldValue === '' ||
    userFieldValue === undefined ||
    userFieldValue === null ||
    (Array.isArray(userFieldValue) && userFieldValue.length === 0)
  );
};

/**
 * Gets the label and value for a field whose parent is a "special type"
 *
 * @param {string} update - The current value for the field which is being updated
 * @param {string} name - The name of the field which is a special type field
 */
const getLabelAndValueForSpecialField = (name, update) => ({
  value: specialFieldChildFieldMap[name].value(update),
  label: specialFieldChildFieldMap[name].label,
});

/**
 * Converts the input value to a string, with special handling for null and numeric values.
 *
 * @param {*} value - The input value to be converted to a string.
 * @returns {string} The input value converted to a string, or an empty string if the input is null.
 */
function convertToString(value) {
  return value === null ? '' : value.toString();
}

/**
 * Expands a list of field identifiers based on predefined mappings and fetches related
 * question UUIDs for compound fields. This function checks each field to determine if it
 * is a compound field as defined in `schema.meta.compound_map` or if it matches a special
 * field type in `specialTypeMap`. For compound fields, it retrieves related questions
 * using their UUID and collects these UUIDs. For special field types, it directly
 * uses the mapped values. Other fields are included in the output as is.
 *
 * @param {string[]} fields - An array of field identifiers to be expanded.
 * @returns {string[]} An array containing the expanded set of identifiers. For compound
 *                     fields, this will be the UUIDs of related questions. For fields
 *                     matched in `specialTypeMap`, it will be the corresponding mapped
 *                     values. All other fields are included unchanged.
 *
 * @example
 * // Assuming `specialTypeMap` are defined elsewhere
 * const fieldsToExpand = ['field1', 'compoundField', 'specialField'];
 * const expandedFields = expandFields(fieldsToExpand);
 * // `expandedFields` will now contain the identifiers for 'field1', any question UUIDs
 * // related to 'compoundField', and any mapped values for 'specialField'.
 */
function expandFields(fields) {
  return fields.reduce((acc, field) => {
    const question = getQuestionByUUID(field);
    const specialQuestion = specialTypeMap[field];

    if (question.type === 'compound') {
      // Check for compound field
      // Fetch and map question UUIDs for the compound field
      return [...acc, ...question.questions.map((q) => q.question_uuid)];
    } else if (specialQuestion) {
      // Check for special field type
      // Use mapped values for the special field type
      return [...acc, ...specialQuestion];
    }
    // Include other fields unchanged
    return [...acc, field];
  }, []);
}

/**
 * Finds a question in the schema by its UUID.
 * It first looks for a direct match in the schema questions.
 * If not found, it checks if the UUID corresponds to a special type and searches again.
 * If still not found, it checks if the UUID is a compound type and searches once more.
 * Throws an error if no matching question is found after all checks.
 *
 * @param {string} uuid The UUID of the question to find.
 * @returns {Object} The found question object.
 */
function getQuestionByUUID(uuid) {
  // Attempt to find a question directly by UUID
  let question = schema.questions.find((question) => question.question_uuid === uuid);

  // check for compound types
  if (!question) {
    const parent = schema.meta.compound_map[uuid];

    if (parent) {
      question = schema.questions
        .find((question) => question.question_uuid === parent)
        .questions.find((question) => question.question_uuid === uuid);
    }
  }

  // check for special types
  if (!question) {
    question = specialFieldChildFieldMap[uuid];
  }

  return question;
}
/**
 * Validates a given field's value against a set of predefined validation rules and returns a specific error label if any validation fails.
 * This function first checks for any special type validations (e.g., US telephone numbers) and then iterates over custom validations defined
 * in the question's `validations` array. Each validation can have an `error_label` associated with it, which is returned in case of validation failure.
 *
 * If the value passes all validations, the function returns `false`, indicating no errors were found. This function is designed to work with a dynamic
 * schema where validations and their corresponding error messages (labels) are defined per field/question.
 *
 * @param {string} fieldKey - The UUID of the field/question to validate.
 * @param {string|number} value - The value to be validated.
 * @returns {string|boolean} A specific error label string if a validation fails, or `false` if all validations pass.
 *
 * @example
 * // Assume 'getQuestionByUUID' returns a question of type 'telephone_us' with a regex validation requiring 10 digits
 * getFieldValidationErrors('some_uuid_for_telephone_us', '(233) 424 - 4244'); // returns false
 * getFieldValidationErrors('some_uuid_for_telephone_us', '123456'); // returns 'aiq_validation_error_regex_telephone_us'
 *
 * @example
 * // Assume another question with UUID 'another_uuid' has a 'required' validation with an error label 'aiq_validation_error_required'
 * getFieldValidationErrors('another_uuid', ''); // returns 'aiq_validation_error_required'
 */
function getFieldValidationErrors(fieldKey, value) {
  const question = getQuestionByUUID(fieldKey);

  // validations for special types
  switch (question.type) {
    case 'telephone_us':
      const regex = new RegExp('^\\(\\d{3}\\) \\d{3} - \\d{4}$'); // matches (233) 424 - 4244

      if (!!value && !regex.test(value)) {
        return 'aiq_validation_error_regex_telephone_us';
      }
  }

  // Check if the field exists and has validations
  if (!question.validations) {
    console.info('Field or validations not defined in schema.');
    return false;
  }

  for (const validation of question.validations) {
    switch (validation.name) {
      case 'required':
        if (validation.argument && (value === undefined || value === null || value === '')) {
          return validation.error_label;
        }
        break;
      case 'regex':
        const regex = new RegExp(validation.argument);
        if (!regex.test(value)) {
          return validation.error_label;
        }
        break;
      case 'min_length':
        if (typeof value === 'string' && value.length < validation.argument) {
          return validation.error_label;
        }
        break;
      case 'max_length':
        if (typeof value === 'string' && value.length > validation.argument) {
          return validation.error_label;
        }
        break;
      case 'min':
        if (typeof value === 'number' && value < validation.argument) {
          return validation.error_label;
        }
        break;
      case 'max':
        if (typeof value === 'number' && value > validation.argument) {
          return validation.error_label;
        }
        break;
      default:
        console.error(`Validation ${validation.name} is not implemented.`);
        Sentry.captureException(`Validation ${validation.name} is not implemented.`);
    }
  }

  // All validations passed
  return false;
}

/**
 * Formats the selection for a multi-select question based on the current state and the new value provided.
 * This function handles three main scenarios:
 * 1. If the new value is 'none', it resets the state to only include 'none'.
 * 2. If the new value is not already in the current state, it adds the value, ensuring no duplicates.
 * 3. If the new value is already in the current state, it removes the value, effectively toggling it.
 *
 * The function returns early with the current state under certain conditions: if the current state does not exist,
 * if the question UUID is not provided, or if the user object is not provided.
 *
 * @param {Object} question - An object representing the question, which should include a `question_uuid` property.
 * @param {string} value - The value to be added to or removed from the current state of selections.
 * @param {Object} user - The user object that holds the current state for the question selections, where the state is keyed by `question_uuid`.
 * @returns {Array<string>} An array representing the updated state of selections for the multi-select question.
 *                          The array may contain a single 'none' value, a combination of selected values, or be empty if the current state is unchanged.
 *
 * @example
 * // Assuming user has previously selected 'option1' for a question with UUID 'q123'
 * const question = { question_uuid: 'q123' };
 * const user = { 'q123': ['option1'] };
 * // Add 'option2' to the selection
 * formatMultiSelect(question, 'option2', user); // Returns ['option1', 'option2']
 *
 * // Remove 'option1' from the selection
 * formatMultiSelect(question, 'option1', user); // Returns ['option2']
 *
 * // Reset selection to 'none'
 * formatMultiSelect(question, 'none', user); // Returns ['none']
 */
const formatMultiSelect = (question, value, user) => {
  const question_uuid = question.question_uuid;
  const currentState = user[question_uuid];

  let newState;
  if (!question_uuid || !user) return currentState;
  if (value === 'none') {
    newState = [value];
  } else if (!currentState?.includes(value)) {
    newState = [...new Set([...(currentState || []), value])].filter((label) => label !== 'none');
  } else {
    newState = currentState.filter((label) => value !== label); // Toggles value
  }

  user[question_uuid] = newState;
  return newState;
};

/**
 * Given an array of objects, this returns a dictonary of key: keyName mapped to value: valueName
 *
 * @param {Object[]} states - An array of state objects.
 * @param {string} keyName - The object property to use as the dictionary key
 * @param {string} valueName - The object property to use as the dictionary value
 * @returns {Object} A dictionary with the new mapping.
 */
const convertArrayToDictonary = (array, keyName, valueName) =>
  array.reduce((acc, obj) => {
    acc[obj[keyName]] = obj[valueName];
    return acc;
  }, {});

const statesByCode = convertArrayToDictonary(Languages.states, 'value', 'name');

/**
 * Converts user update options to a comma-separated string of option labels.
 *
 * @param {Object} translations - JSON object of translations in the RN codebase
 * @param {Object} userUpdates - The updates made by the user, keyed by update field.
 * @param {string} updateField - The field being updated, to lookup in userUpdates and questions.
 * @returns {string} A comma-separated string of option labels for the update field.
 */
function getUserUpdateOptionsAsString(translations, userUpdates, updateField) {
  const question = getQuestionByUUID(updateField);
  if (!question) {
    console.error(`getUserUpdateOptionsAsString: Undefined schema for field: ${updateField}`);
    Sentry.captureException(`getUserUpdateOptionsAsString: Undefined schema for field: ${updateField}`);
    return;
  }

  return userUpdates[updateField]
    .map((value) => {
      const option = question.options.find((option) => option.value === value);
      if (!option) {
        console.error(`getUserUpdateOptionsAsString: Undefined option for option: ${value}`);
        Sentry.captureException(`getUserUpdateOptionsAsString: Undefined option for option: ${value}`);
        return;
      }
      return translations[option.label];
    })
    .join(', ');
}

/**
 * Method to get the fields that are requested by a pantry
 * @param {object} user   The user object
 * @param {object} pantry The pantry object
 * @returns {[requestedFields, missingFields]}      The requested fields array and missing fields array whether the field is missing on the user object
 */
function getAdditionalRequestedFields(user, pantry) {
  const isEmpty = (rec) => {
    return (
      user[rec] === undefined ||
      user[rec] === null ||
      user[rec] === '' ||
      (Array.isArray(user[rec]) && user[rec].length === 0)
    );
  };
  const isMissing = (field) => {
    return pantry.allowed_fields.indexOf(field) == -1 && isEmpty(field);
  };

  // requested fields are all additional info fields requested by the pantry
  let requestedFields = [];

  // missing fields are all additional info fields requested by the pantry that are missing on the user object
  const missingFields = [];

  if (pantry.requested_fields && pantry.requested_fields.length) {
    pantry.requested_fields.forEach((field) => {
      if (ignoreFields.includes(field)) {
        return;
      }

      const fieldGroup = expandFields([field]);

      if (fieldGroup) {
        // if any of the fields in the group is missing, then the whole group is missing
        if (fieldGroup.some(isMissing)) {
          missingFields.push(field);
        } else {
          requestedFields.push(field);
        }
      } else {
        if (isMissing(field)) {
          missingFields.push(field);
        } else {
          requestedFields.push(field);
        }
      }
    });
  }

  // missing fields are always a subset of requested fields
  requestedFields = [...requestedFields, ...missingFields];

  return [requestedFields, missingFields];
}
